import React from 'react';
import { User } from 'lucide-react';
import { ResponsiveImage } from '../../ResponsiveImage';

/**
 * Store cover image with the store name overlaid and the owner profile picture
 * rendered as an overlapping avatar. Falls back to a soft brand gradient when
 * no cover image is provided.
 */
export function StoreCover({ store }) {
    return (
        <section
            data-store-cover
            className="relative w-full max-w-5xl mx-auto shadow-xl bg-surface"
            style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 60px)', marginTop: 0 }}
        >
            <div data-store-cover-media className="relative h-48 sm:h-64 md:h-72 w-full overflow-hidden rounded-t-2xl">
                {store.cover_image_url ? (
                    <ResponsiveImage
                        src={store.cover_image_url}
                        alt="Store Cover"
                        fillContainer={true}
                        placeholderClassName="bg-gradient-to-br from-brand-200 via-brand-100 to-brand-50"
                        className="absolute inset-0"
                        containerClassName="absolute inset-0"
                    />
                ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-brand-200 via-brand-100 to-brand-50" style={{ zIndex: 1 }} />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" style={{ zIndex: 2 }} />
                <div className="absolute left-0 bottom-0 w-full px-6 pb-3" style={{ zIndex: 3 }}>
                    <div className="flex items-end justify-between gap-4 w-full">
                        <div className="flex-1 order-2 sm:order-1 min-w-0">
                            <h1
                                data-store-name
                                className="text-2xl sm:text-3xl font-bold text-white drop-shadow-lg mb-1 break-words"
                                style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                            >
                                {store.name}
                            </h1>
                        </div>
                        {/* Owner profile image - mobile only */}
                        <div className="order-1 sm:order-2 sm:invisible">
                            {store.owner_profile_url ? (
                                <div className="w-20 h-20 sm:w-32 sm:h-32 rounded-pill border-2 border-white shadow-lg overflow-hidden flex-shrink-0">
                                    <ResponsiveImage
                                        src={store.owner_profile_url}
                                        alt="Owner"
                                        placeholderClassName="bg-brand-200"
                                        aspectRatio="aspect-square"
                                    />
                                </div>
                            ) : (
                                <div className="w-20 h-20 sm:w-32 sm:h-32 rounded-pill bg-brand-200 border-2 border-white flex items-center justify-center shadow-lg">
                                    <User className="w-10 h-10 sm:w-16 sm:h-16 text-brand-500" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Owner profile image - desktop, overlapping bottom of cover */}
            <div
                className="hidden sm:flex justify-center absolute left-0 right-0 z-floating"
                style={{ top: 'calc(100% - 65px)', pointerEvents: 'none' }}
            >
                {store.owner_profile_url ? (
                    <div className="w-32 h-32 rounded-pill border-4 border-white shadow-lg overflow-hidden flex-shrink-0 bg-white">
                        <ResponsiveImage
                            src={store.owner_profile_url}
                            alt="Owner"
                            placeholderClassName="bg-brand-200"
                            aspectRatio="aspect-square"
                        />
                    </div>
                ) : (
                    <div className="w-32 h-32 rounded-pill bg-brand-200 border-4 border-white flex items-center justify-center shadow-lg">
                        <User className="w-16 h-16 text-brand-500" />
                    </div>
                )}
            </div>
        </section>
    );
}

export default StoreCover;
